// Clinical rationale commentary for the Veterinary Anaesthesia Simulator 2 plan page.
// Explains WHY each agent is chosen (role + advantages + cautions) and surfaces
// patient-specific considerations derived from species / ASA status / breed.
import { getBreedWatch, getBreedVulnerabilities } from "@/data/breedVulnerabilities";

export const DRUG_RATIONALE = {
  // ---------- Pre-medication ----------
  acepromazine: {
    role: "Sedation & anxiolysis",
    why: "Smooths induction and reduces the dose of induction and maintenance agents (MAC-sparing). Antiemetic and protects against catecholamine-induced arrhythmias.",
    watch:
      "No analgesia and no reversal agent. Causes vasodilation and hypotension — avoid in hypovolaemic, shocked or unstable patients.",
  },
  methadone: {
    role: "Analgesia — full µ-opioid",
    why: "Reliable peri-operative analgesia with marked MAC-sparing. Its NMDA activity helps prevent central pain 'wind-up' — a strong choice for genuinely painful or surgical cases.",
    watch: "Dose-dependent respiratory depression and bradycardia; may cause panting and reduces GI motility.",
  },
  buprenorphine: {
    role: "Analgesia — partial µ-opioid",
    why: "Long-acting (6–8 h) with a ceiling on respiratory depression — well suited to mild-to-moderate pain and lower-risk patients.",
    watch: "Slow onset and a ceiling effect make it hard to 'top up' with a full agonist later; inadequate alone for severe surgical pain.",
  },
  butorphanol: {
    role: "Sedation + mild analgesia",
    why: "A useful sedative co-medication with good antitussive/visceral effect for short, minimally-painful procedures such as imaging or endoscopy.",
    watch: "Weak, short-lived somatic analgesia with a ceiling effect — not sufficient on its own for surgery.",
  },
  dexmedetomidine: {
    role: "Sedation & analgesia — alpha-2 agonist",
    why: "Profound, reliable sedation with genuine analgesia and large MAC-sparing, and it is fully reversible with atipamezole — excellent value in healthy (ASA I–II) patients.",
    watch:
      "Marked initial hypertension followed by bradycardia and reduced cardiac output. Avoid in cardiac disease and compromised (ASA III+) patients.",
  },
  midazolam: {
    role: "Co-induction & muscle relaxation — benzodiazepine",
    why: "Negligible cardiovascular effect makes it a cornerstone of 'balanced' protocols for sick or geriatric patients, reducing the induction-agent dose. Reversible with flumazenil.",
    watch: "Unreliable sedation alone in healthy young animals (can cause excitement/dysphoria); best paired with an opioid.",
  },
  atropine: {
    role: "Anticholinergic — vagolytic",
    why: "Rapid onset makes it the agent of choice for acute, vagally-mediated bradycardia.",
    watch: "Causes transient tachycardia and raises myocardial O₂ demand; thickens secretions. Reserve for indicated bradycardia rather than routine use.",
  },
  glycopyrrolate: {
    role: "Anticholinergic — vagolytic",
    why: "Longer-acting and smoother heart-rate support than atropine, and it does not cross the blood-brain or placental barrier — useful for sustained vagal stimulation.",
    watch: "Slower onset than atropine; still increases myocardial O₂ demand.",
  },

  // ---------- Induction agents ----------
  propofol: {
    role: "IV induction",
    why: "Smooth, rapid induction with fast clearance and a clear-headed recovery — the default for most healthy patients and ideal when quick airway control is needed.",
    watch: "Dose-dependent vasodilation, hypotension and apnoea — titrate slowly to effect. Provides no analgesia.",
  },
  alfaxalone: {
    role: "IV induction — neuroactive steroid",
    why: "Good cardiovascular stability with a wide safety margin — a strong choice for compromised, cardiac or sighthound patients, and it can be given IM in fractious cats.",
    watch: "Titrate to effect to avoid apnoea; recovery can be rough without adequate premedication.",
  },
  ketamine_diazepam: {
    role: "Co-induction — dissociative + benzodiazepine",
    why: "Maintains sympathetic tone and blood pressure while providing analgesia and anti-wind-up — favoured for shock, trauma and unstable (ASA III–IV) patients.",
    watch: "Raises heart rate and myocardial O₂ demand; avoid in hypertrophic/obstructive cardiac disease. Relies on hepatic/renal clearance.",
  },
  thiopental: {
    role: "IV induction — barbiturate",
    why: "Very rapid, smooth induction historically valued for fast airway control.",
    watch:
      "Pro-arrhythmic (bigeminy), profoundly respiratory-depressant and severely tissue-irritant if perivascular. Redistributes to fat — AVOID in sighthounds.",
  },
  etomidate: {
    role: "IV induction — imidazole",
    why: "Outstanding cardiovascular stability — reserved for severe cardiac disease or critically unstable patients in whom any hypotension is dangerous.",
    watch: "Must be co-induced (myoclonus, pain on injection) and suppresses adrenocortical function — single induction dose only.",
  },
};

export const MAINTENANCE_RATIONALE = {
  isoflurane: {
    role: "Volatile maintenance",
    why: "Predictable depth control with minimal metabolism (~0.2%) and a well-characterised MAC — the cost-effective workhorse volatile for routine cases.",
    watch: "Dose-dependent vasodilation/hypotension and respiratory depression; pungent, so poorly suited to mask induction.",
  },
  sevoflurane: {
    role: "Volatile maintenance",
    why: "Low blood-gas solubility allows fast changes in depth and a quick recovery, and it is non-pungent — ideal for mask/chamber induction and for high-risk or brachycephalic airways where rapid control matters.",
    watch: "More expensive than isoflurane and may need higher fresh-gas flows; similar dose-dependent hypotension.",
  },
};

const asaLevel = (patient) => {
  const a = String(patient?.asa || "");
  if (a.includes("V")) return 5;
  if (a.includes("IV")) return 4;
  if (a.includes("III")) return 3;
  if (a.includes("II")) return 2;
  return 1;
};

// Case-level considerations summarising species, ASA status and breed watch-points.
export function caseConsiderations(patient) {
  if (!patient) return [];
  const notes = [];
  const asa = asaLevel(patient);
  if (asa >= 3) {
    notes.push(
      `Higher anaesthetic risk (ASA ${patient.asa}): favour cardiovascularly-stable, reversible and MAC-sparing agents, titrate carefully, and generally avoid acepromazine and alpha-2 agonists.`
    );
  } else {
    notes.push(
      `Lower anaesthetic risk (ASA ${patient.asa}): a healthy patient tolerates a standard balanced premed-induction-volatile protocol well.`
    );
  }
  if (patient.species === "cat") {
    notes.push(
      "Feline patient: use cat-specific doses, pre-oxygenate, and minimise airway handling to reduce the risk of laryngospasm."
    );
  }
  const watch = getBreedWatch(patient);
  if (watch) notes.push(`${patient.breed}: ${watch}.`);
  if (patient.procedure) {
    notes.push(
      `Procedure (${patient.procedure}): match the depth of analgesia and anaesthetic plane to the expected surgical stimulation.`
    );
  }
  return notes;
}

// Optional patient-specific flag for a chosen agent: { tone: "warn" | "good", text }.
export function caseFlag(drugId, patient) {
  if (!patient) return null;
  const asa = asaLevel(patient);
  const watch = getBreedWatch(patient) || "";
  const vulns = getBreedVulnerabilities(patient);
  const cardiac = vulns.includes("arrhythmia") || /HCM|MVD|DCM/i.test(watch);
  const brachy = /BOAS|brachycephalic/i.test(watch);
  const sighthound = /sighthound/i.test(watch);

  switch (drugId) {
    case "acepromazine":
      if (asa >= 3)
        return {
          tone: "warn",
          text: `Caution in ASA ${patient.asa}: vasodilation/hypotension with no reversal — usually avoided in compromised patients.`,
        };
      break;
    case "dexmedetomidine":
      if (cardiac)
        return {
          tone: "warn",
          text: "Caution with this patient's cardiac risk: marked bradycardia and reduced cardiac output — consider avoiding or use a low dose.",
        };
      if (asa >= 3)
        return {
          tone: "warn",
          text: `Caution in ASA ${patient.asa}: significant cardiovascular depression — favour a benzodiazepine-opioid co-induction instead.`,
        };
      break;
    case "thiopental":
      if (sighthound)
        return {
          tone: "warn",
          text: "Avoid in sighthounds: low body-fat reserves cause a markedly prolonged recovery — choose alfaxalone or propofol.",
        };
      break;
    case "ketamine_diazepam":
      if (cardiac)
        return {
          tone: "warn",
          text: "Caution: raises heart rate and myocardial O₂ demand — avoid in hypertrophic/obstructive cardiac disease.",
        };
      break;
    case "alfaxalone":
      if (asa >= 3 || cardiac || sighthound)
        return {
          tone: "good",
          text: "Good fit here: cardiovascularly-stable induction with a wide safety margin for this higher-risk patient.",
        };
      break;
    case "etomidate":
      if (cardiac || asa >= 4)
        return {
          tone: "good",
          text: "Excellent cardiovascular stability for this high-risk / cardiac patient — co-induce with a benzodiazepine.",
        };
      break;
    case "midazolam":
      if (asa >= 3)
        return {
          tone: "good",
          text: "Good fit: negligible cardiovascular effect supports a balanced protocol in this compromised patient.",
        };
      break;
    case "isoflurane":
      if (brachy)
        return {
          tone: "warn",
          text: "Brachycephalic airway: sevoflurane allows faster control of depth and a quicker recovery — consider switching.",
        };
      break;
    case "sevoflurane":
      if (brachy)
        return {
          tone: "good",
          text: "Good fit: rapid, non-pungent control of depth and faster recovery for this brachycephalic / airway-risk patient.",
        };
      break;
    default:
      return null;
  }
  return null;
}

// Short rationale for where the chosen dose sits within the recommended range,
// contextualised to the patient's ASA status.
export function doseRationale(drug, dose, patient, species) {
  const range = species === "cat" ? drug.doseCat : drug.doseDog;
  if (!range || dose == null || isNaN(dose)) return null;
  const span = range.high - range.low;
  const frac = span > 0 ? (dose - range.low) / span : 0.5;
  const asa = asaLevel(patient);

  if (frac <= 0.25) {
    return {
      band: "Low end of range",
      tone: "info",
      text: "Conservative dosing — appropriate for compromised, cardiac, geriatric or already-sedated patients, and when combined with other CNS depressants.",
    };
  }
  if (frac >= 0.75) {
    if (asa >= 3) {
      return {
        band: "High end of range",
        tone: "warn",
        text: `Top-of-range dose in an ASA ${patient.asa} patient — consider reducing to limit cardiorespiratory depression and hypotension.`,
      };
    }
    return {
      band: "High end of range",
      tone: "info",
      text: "Near-maximal effect but greater cardiorespiratory depression — reserve for young, healthy or highly-stimulated patients and titrate to effect.",
    };
  }
  return {
    band: "Mid-range dose",
    tone: "info",
    text: "A standard balanced dose for a typical patient of this status.",
  };
}

const OPIOID_CLASS = {
  methadone: "full",
  buprenorphine: "partial",
  butorphanol: "agonist-antagonist",
};
const PAINFUL_PROCEDURE =
  /extract|surg|spay|ovari|castrat|neuter|orthop|fractur|repair|mass|removal|laparotom|enterotom|amputat|cystotom|explorator|biops|cruciat|tplo|lump|resection|dental/i;

// Detects problematic (or notably good) combinations in the current protocol.
// Returns an array of { tone: "warn" | "good", text }.
export function combinationFlags(plan, patient) {
  const flags = [];
  const pm = plan?.premeds || [];
  const has = (id) => pm.includes(id);
  const opioidsSelected = pm.filter((id) => OPIOID_CLASS[id]);
  const hasOpioid = opioidsSelected.length > 0;
  const hasFullMu = has("methadone");
  const painful = PAINFUL_PROCEDURE.test(String(patient?.procedure || ""));

  if (has("butorphanol") && hasFullMu) {
    flags.push({
      tone: "warn",
      text: "Butorphanol (agonist-antagonist) can partially reverse methadone's µ-opioid analgesia — don't combine a full µ-agonist with an agonist-antagonist.",
    });
  }
  if (has("buprenorphine") && hasFullMu) {
    flags.push({
      tone: "warn",
      text: "Buprenorphine's high receptor affinity can blunt methadone and is hard to 'top up' — avoid stacking a partial and a full µ-opioid.",
    });
  }
  if (painful && !hasOpioid) {
    flags.push({
      tone: "warn",
      text: "This procedure is expected to be painful but no opioid analgesia is planned — add a full µ-opioid such as methadone.",
    });
  } else if (painful && !hasFullMu && !has("buprenorphine") && has("butorphanol")) {
    flags.push({
      tone: "warn",
      text: "Butorphanol gives only weak, short-lived somatic analgesia — likely inadequate for this procedure's pain. Prefer a full µ-opioid.",
    });
  }
  if (has("acepromazine") && !hasOpioid) {
    flags.push({
      tone: "warn",
      text: "Acepromazine provides sedation but NO analgesia — pair it with an opioid if any pain is anticipated.",
    });
  }
  if (has("acepromazine") && has("dexmedetomidine")) {
    flags.push({
      tone: "warn",
      text: "Stacking acepromazine (vasodilation) with dexmedetomidine (bradycardia) risks profound hypotension and bradycardia — reduce doses and monitor closely.",
    });
  }
  if ((has("atropine") || has("glycopyrrolate")) && has("dexmedetomidine")) {
    flags.push({
      tone: "warn",
      text: "Routinely combining an anticholinergic with an alpha-2 agonist is controversial — it can cause marked hypertension and increased myocardial work.",
    });
  }
  if (pm.length === 0) {
    flags.push({
      tone: "warn",
      text: "No premedication selected — expect a rougher induction, a higher induction-agent requirement, and no pre-emptive analgesia.",
    });
  }
  const hasSedative =
    has("acepromazine") || has("dexmedetomidine") || has("midazolam");
  if (
    hasOpioid &&
    opioidsSelected.length === 1 &&
    hasSedative &&
    !(has("acepromazine") && has("dexmedetomidine"))
  ) {
    flags.push({
      tone: "good",
      text: "Balanced premedication: a sedative plus a single analgesic opioid gives reliable sedation, analgesia and MAC-sparing.",
    });
  }
  return flags;
}
